import {
  PaginatedParams,
  FilterParams,
  PaginatedData
} from "src/library/utils";
import useAxios from "src/core/shared/hooks/useAxios";
import { IOrder } from "src/core/orders/api/orders.api";
import { IProcessJob } from "src/core/process-job/api/processJobs.api";
import {
  EndpointConfig,
  UseApiRequest,
  RequestConfig
} from "src/core/shared/api/config.api";

export type ListOrderExportParams = { pagination: PaginatedParams<IOrderExport>; filters: FilterParams<IOrderExport> };
export type ExportOrdersParams = { orderExportSettingId: number; filters: FilterParams<IOrder>; archiveMockDateTime?: Date; };
export type ExportOrdersBySettingNameParams = { name: string; filters: FilterParams<IOrder> };

type OrderExportEndpointConfig = {
  LIST_EXPORTS: EndpointConfig<
  ListOrderExportParams,
  PaginatedData<IOrderExport>
  >;
  EXPORT_ORDERS: EndpointConfig<
  ExportOrdersParams,
  IProcessJob
  >;
  LIST_ORDER_EXPORT_SETTINGS: EndpointConfig<
  null,
  IOrderExportSetting[]
  >;
  EXPORT_ORDERS_BY_SETTING_NAME: EndpointConfig<
    ExportOrdersBySettingNameParams,
    IProcessJob
  >;
};

const requests: RequestConfig<OrderExportEndpointConfig> = {
  LIST_EXPORTS: params => ({
    url: `${process.env.REACT_APP_API_URL}/order-export/all`,
    method: "GET",
    params
  }),
  EXPORT_ORDERS: ({ orderExportSettingId, ...data }: ExportOrdersParams) => ({
    url: `${process.env.REACT_APP_API_URL}/order-export/${orderExportSettingId}`,
    method: "POST",
    data
  }),
  LIST_ORDER_EXPORT_SETTINGS: data => ({
    url: `${process.env.REACT_APP_API_URL}/order-export/settings/all`,
    method: "GET",
    data
  }),
  EXPORT_ORDERS_BY_SETTING_NAME: data => ({
    url: `${process.env.REACT_APP_API_URL}/order-export/setting-name/export`,
    method: "POST",
    data
  })
};

export const useOrderExportApi: UseApiRequest<OrderExportEndpointConfig> = name => useAxios(requests[ name ], name);

export interface IOrderExport {
  id?: number;
  s3BucketUrl: string;
  exportedAt: Date;
  totalCount: number;
  type: OrderExportType;
  processJobId?: number;
  processJob?: IProcessJob;
}

export interface IOrderExportSetting {
  id: number;
  name: string;
  type: OrderExportType;
  description: string;
  options: OrderExportSettingOptions;
  headerMappings: Array<ExportFieldMapping<OrderExportLine>>;
}

export interface OrderExportSettingOptions {
  fileName: OrderExportFileName;
  sftp?: OrderExportSFTPSetting;
  extraDays?: number;
  exportBy?: OrderExportSettingBy;
  exportableStatusCodes?: string[];
  exportStatusCode?: string;
  sendTo: string[];
  hidden?: boolean;
}
export enum OrderExportType {
  SinceLast = "since_last",
  Report = "report",
  Archive = "archive"
}

export interface OrderExportFileName {
  template: string;
  dateFormat: string;
}

export type OrderExportCSVHeading = keyof OrderExportLine;

export type OrderExportSettingBy = "touched" | "cutoff";

export interface ExportFieldMapping<ImportJSON> {
  custom?: boolean;
  external: string;
  internal: keyof ImportJSON;
  defaultValue?: string;
  dateFormat?: string;
}

export interface OrderExportSFTPSetting {
  secretArn: string;
  directory: string;
  notifyErrors: string[];
}

export interface OrderExportLine {
  dropId: number;
  orderId: number;
  orderLineId: number;
  productId: number;
  branchNumber?: string;
  partNumber?: string;
  poNumber?: string;
  quantity: number;
  deliveryDate: Date | string | null;
  custom?: { [key: string]: string | number };
  orderStatus?: string;
  replacementPartNumber?: string;
  externalId?: string;
}
